import type { Payment } from "@/services/api/payments";
import { getDisplayName, getInitials } from "@/lib/user";
import { formatINR } from "@/lib/currency";
import { formatDate } from "@/lib/dateFormat";
import { formatRoom } from "@/pages/Members/lib/memberFormat";
import StatusBadge from "@/components/StatusBadge/StatusBadge";
import type { StatusTone } from "@/components/StatusBadge/StatusBadge";
import RowActionsMenu from "@/components/ActionCells/RowActionsMenu";
import type { RowAction } from "@/components/ActionCells/RowActionsMenu";
import styles from "../Payments.module.scss";

export interface MockSubscriptionOverlay {
  room?: string;
  started?: string;
  renews?: string;
  autoRenew?: boolean;
}

interface PaymentCardProps {
  payment: Payment;
  actions: RowAction[];
  overlay?: MockSubscriptionOverlay;
  paymentTypeLabel: string;
  statusTone: StatusTone;
  statusLabel: string;
  userTypeLabel: string;
}

const PaymentCard = ({
  payment,
  actions,
  overlay,
  paymentTypeLabel,
  statusTone,
  statusLabel,
  userTypeLabel,
}: PaymentCardProps) => {
  const memberName = getDisplayName(payment, { fallbackLabel: "Payer" });
  const initials = getInitials(memberName);
  const formattedAmount = formatINR(payment.amount);
  const room = formatRoom(payment.userCategory) ?? overlay?.room ?? null;
  const isAutoRenewOn = payment.autoRenewal !== undefined && payment.autoRenewal !== null
    ? Boolean(payment.autoRenewal)
    : Boolean(overlay?.autoRenew);
  const started = formatDate(payment.subscriptionStartedAt || payment.createdAt, {
    fallback: overlay?.started || "—",
  });
  const renews = formatDate(payment.renewsAt, {
    fallback: overlay?.renews || "—",
  });

  return (
    <div className={styles.mobileCard}>
      {/* Top Header: Avatar, Name, User Type, Status & Actions */}
      <div className={styles.mobileCardHeader}>
        <div className={styles.mobileAvatar}>
          <span>{initials}</span>
        </div>

        <div className={styles.mobileMemberInfo}>
          <h3 className={styles.mobileMemberName}>{memberName}</h3>
          <span className={styles.mobileMemberType}>{userTypeLabel}</span>
        </div>

        <div className={styles.mobileCardActions}>
          <StatusBadge
            label={statusLabel}
            tone={statusTone}
            className={styles.mobileStatusBadge}
          />
          <RowActionsMenu
            actions={actions}
            ariaLabel={`Actions for ${memberName}`}
            triggerClassName={styles.mobileRowActionTrigger}
          />
        </div>
      </div>

      {/* Amount and Room Row */}
      <div className={styles.mobileAmountRow}>
        <div className={styles.mobileAmountBlock}>
          <span className={styles.mobileAmountLabel}>Amount</span>
          <span className={styles.mobileAmountValue}>{formattedAmount}</span>
        </div>

        {room ? (
          <span className={styles.mobileRoomBadge}>{room}</span>
        ) : (
          <span className={styles.mobileRoomNone}>—</span>
        )}
      </div>

      <div className={styles.mobileCardDivider} />

      {/* 2x2 Details Grid */}
      <div className={styles.mobileCardGrid}>
        <div className={styles.mobileCardField}>
          <span className={styles.mobileFieldLabel}>Method</span>
          <span className={styles.mobileFieldValue}>{paymentTypeLabel}</span>
        </div>

        <div className={styles.mobileCardField}>
          <span className={styles.mobileFieldLabel}>Auto-Renew</span>
          <span className={styles.mobileFieldValue}>
            <span
              className={
                isAutoRenewOn
                  ? styles.autoRenewActive
                  : styles.autoRenewInactive
              }
            >
              {isAutoRenewOn ? "On" : "Off"}
            </span>
          </span>
        </div>

        <div className={styles.mobileCardField}>
          <span className={styles.mobileFieldLabel}>Started</span>
          <span className={styles.mobileFieldValue}>{started}</span>
        </div>

        <div className={styles.mobileCardField}>
          <span className={styles.mobileFieldLabel}>Renews</span>
          <span className={styles.mobileFieldValue}>{renews}</span>
        </div>
      </div>
    </div>
  );
};

export default PaymentCard;
